import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet's default marker path when bundled by webpack.
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function ClickHandler({ onChange }) {
  useMapEvents({
    click(e) {
      onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      map.setView([lat, lng]);
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * A tiny OpenStreetMap picker used by the tenant admin worksite editor.
 * Emits `onChange({ lat, lng })` on click. Draws the current fence radius
 * (in meters) as a circle so operators can eyeball coverage.
 */
export default function GeofenceMapPicker({
  lat,
  lng,
  radius_meters = 100,
  onChange,
  testId = "geofence-map",
  interactive = true,
}) {
  const [center, setCenter] = useState({
    lat: typeof lat === "number" ? lat : 40.7128,
    lng: typeof lng === "number" ? lng : -74.006,
  });

  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      setCenter({ lat, lng });
    }
  }, [lat, lng]);

  const zoom = useMemo(() => (radius_meters > 400 ? 15 : radius_meters > 200 ? 16 : 17), [radius_meters]);

  const handle = (next) => {
    setCenter(next);
    if (onChange) onChange(next);
  };

  return (
    <div
      className="overflow-hidden rounded-lg border border-border"
      style={{ height: 320 }}
      data-testid={testId}
    >
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter lat={center.lat} lng={center.lng} />
        {interactive && <ClickHandler onChange={handle} />}
        <Marker position={[center.lat, center.lng]} />
        <Circle
          center={[center.lat, center.lng]}
          radius={radius_meters}
          pathOptions={{ color: "#2563eb", fillOpacity: 0.12 }}
        />
      </MapContainer>
    </div>
  );
}
